import { Button } from "@mui/material";
import { useState } from "react";
import { listHeaderItems, renderListStore } from "../../common/listStoreUi";
import { createListStore } from "../../common/listStore";
import { UseEntityStore } from "../../common/entityStore";
import { map } from "rxjs";
import { useDialog, useSimpleDialog, yesNoButtons } from "../../common/dialog";
import { CustomerAddressDialog, NewAddressId } from "./CustomerAddressDialog";
import { Add, Delete, Edit } from "@mui/icons-material";
import { CustomerAddressDto, TmOrderDto } from "./common";
import { sortBy } from "lodash";
import { getResource } from "../../common/localization";

function getText(key: string, defaultMessage?: string) {
    return getResource("Ui.Customer." + key, defaultMessage) as string;
}

export function CustomerAddressList(props: {
    useCustomerStore: UseEntityStore<TmOrderDto>;
    disabled: boolean;
    canManageExisting?: false;
    customItemSelect?(item: CustomerAddressDto): void;
}) {
    const [useStore] = useState(() => createListStore({
        data: props.useCustomerStore.getState().clientData$.pipe(map(p => ({
            items: sortBy(p.data.customerAddresses, p => p.id).map(p => ({ ...p, strt: p.strt1 + " " + p.strt2 })),
            isFetching: p.isFetching,
        }))),
        getId: p => p.id,
        itemsPerPage: 5,
        listProps: [
            { column: "fname", label: getText("FirstName"), width: 110 },
            { column: "lname", label: getText("LastName"), width: 130 },
            { column: "coadr", label: getText("CoAdr"), width: 130 },
            { column: "strt", label: getText("Strt"), width: 150, },
            { column: "city", label: getText("City"), width: 150, },
            { column: "zipCode", label: getText("ZipCode"), width: 80, },
        ],
    }));
    const newDialog = useDialog(close => <CustomerAddressDialog close={close} customerAddress={null}
        useCustomerStore={props.useCustomerStore} disabled={props.disabled} />, "md");
    const { selectedItem, canAddNewAddress } = useStore(p => ({
        selectedItem: p.selectedItem,
        canAddNewAddress: !props.disabled && !p.items.some(p => p.id === NewAddressId),
    }));
    const editDialog = useDialog(close => <CustomerAddressDialog close={close} customerAddress={selectedItem!}
        useCustomerStore={props.useCustomerStore} disabled={props.disabled} />, "md");
    const simpleDialog = useSimpleDialog();
    async function deleteAddress() {
        await simpleDialog.show(getText("DeleteDeliiveryAddress.Title"), getText("DeleteDeliveryAddress.Content"), undefined, yesNoButtons(async () => {
            const addresses = props.useCustomerStore.getState().clientData.customerAddresses!.filter(p => p.id !== selectedItem!.id);
            props.useCustomerStore.getState().setClientDataProp("customerAddresses", addresses);
        }));
    }
    const canManageExisting = props.canManageExisting ?? true;
    const canManageSelectedItem = !props.disabled && selectedItem != null && (canManageExisting || selectedItem.id === NewAddressId);
    function addAddress() {
        const newItem = useStore.getState().items.find(p => p.id === NewAddressId);
        if (newItem != null) {
            useStore.getState().setSelectedItem(newItem);
            editDialog.show();
        } else {
            newDialog.show();
        }
    }
    return <>
        {newDialog.render()}
        {editDialog.render()}
        {simpleDialog.render()}
        {renderListStore(useStore, {
            onDoubleClick: item => {
                if (props.customItemSelect) {
                    props.customItemSelect(item);
                    return;
                }
                if (!canManageExisting && item.id !== NewAddressId) {
                    return;
                }
                editDialog.show();
            }, header: listHeaderItems([
                ["edit", () => <Button color="edit" disabled={!canManageSelectedItem} title={getResource("Ui.Edit") as string} onClick={() => editDialog.show()}><Edit /></Button>],
                ["add", () => <Button color="add" title={getResource("Ui.Add") as string} onClick={addAddress} disabled={!canAddNewAddress}><Add /></Button>],
                ["remove", () => <Button color="delete" disabled={!canManageSelectedItem} title={getResource("Ui.Delete") as string} onClick={deleteAddress}><Delete /></Button>],
            ]),
        })}
    </>;
}
